/**
 * The four languages this app ships — a straight port of Android's
 * `core/locale/AppLocale.kt`, deliberately kept in the same order so the picker
 * in the two apps reads identically to somebody holding both phones.
 *
 * `nativeName` is hardcoded rather than translated, exactly as on Android: a
 * person who cannot read the language the app is *currently* in still has to be
 * able to find their own, so every entry is always written in its own script.
 */
export interface AppLocaleDef {
  /** BCP-47 tag. Also what gets reported to the backend as `meta.appLanguage`. */
  tag: 'vi' | 'en' | 'zh-CN' | 'pt-BR';
  nativeName: string;
  /** Compact form for the app-bar chip, where the full name will not fit. */
  shortCode: string;
}

const VIETNAMESE: AppLocaleDef = { tag: 'vi', nativeName: 'Tiếng Việt', shortCode: 'VI' };
const ENGLISH: AppLocaleDef = { tag: 'en', nativeName: 'English', shortCode: 'EN' };
const CHINESE_SIMPLIFIED: AppLocaleDef = { tag: 'zh-CN', nativeName: '中文（简体）', shortCode: '中文' };
const PORTUGUESE_BR: AppLocaleDef = { tag: 'pt-BR', nativeName: 'Português (Brasil)', shortCode: 'PT' };

/** Picker order. Android's enum declaration order — VI, EN, ZH-CN, PT-BR. */
export const LOCALES: readonly AppLocaleDef[] = [
  VIETNAMESE,
  ENGLISH,
  CHINESE_SIMPLIFIED,
  PORTUGUESE_BR,
];

/**
 * Fallback for anything we do not translate. **English, never Vietnamese** —
 * the app was written in Vietnam and the temptation to default to it is real,
 * but a Japanese or Spanish device landing on Vietnamese text is unreadable in
 * a way that landing on English is not. Same choice as Android's `DEFAULT`.
 */
export const DEFAULT_LOCALE_TAG: AppLocaleDef['tag'] = 'en';

/**
 * Maps whatever tag the browser reports onto a language we ship.
 *
 * Matching is on the **language subtag only**. `navigator.language` hands back
 * wildly different shapes depending on iOS version and region settings ("zh",
 * "zh-CN", "zh-Hans-CN", occasionally with "_" instead of "-"), and for our
 * purposes they all mean the same thing. Port of `AppLocale.resolve`.
 */
export function resolveLocale(tag: string | null | undefined): AppLocaleDef['tag'] {
  const language = (tag ?? '').trim().replace(/_/g, '-').split('-')[0]?.toLowerCase() ?? '';

  switch (language) {
    case 'vi':
      return 'vi';
    case 'zh':
      return 'zh-CN';
    // pt-PT is folded into pt-BR on purpose: Brazilian Portuguese reads far
    // closer to European Portuguese than English does.
    case 'pt':
      return 'pt-BR';
    case 'en':
      return 'en';
    default:
      return DEFAULT_LOCALE_TAG;
  }
}

/**
 * The definition behind a tag.
 *
 * The fallback is unreachable — `LOCALES` covers every member of the tag union —
 * but `find` cannot know that, and returning a synthesised object here instead
 * would duplicate the native names in a second place that could drift.
 */
export function localeFor(tag: AppLocaleDef['tag']): AppLocaleDef {
  return LOCALES.find((locale) => locale.tag === tag) ?? ENGLISH;
}
